/**
 * Profit Report data: demo fixtures or live truth v2 → ProfitPageBundle.
 */

import { fetchOrdersDailySeries } from '../services/ordersDailyTruthClient';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getMockProfitReport,
  type ProfitPageBundle,
  type ProfitReportPeriod,
  type ProfitRevenueImputationNotice,
  type ProfitXeroKpiNotice,
} from '../data/mockProfitPage';
import {
  fetchTruthCustomerProfitabilityDaily,
  fetchTruthMergedCosts,
  fetchTruthMergedOrders,
  fetchTruthProfitKpis,
  fetchTruthStatus,
  type TruthProfitKpisFetchResult,
  type TruthRevenueImputationMeta,
} from '../services/profitabilityTruthClient';
import type { TruthMergedCostRow, TruthMergedOrderRow } from '../types/profitabilityTruth';
import { emptyProfitReportShell } from '../utils/emptyProfitReportShell';
import { buildDailySeries, otherWorkLineText, profitPageFromTruth, revenueImputationMonthsText } from '../utils/profitPageFromTruth';
import { API_ENDPOINTS } from '../config/api';
import { profitReportChartRange, profitReportPeriodToRange } from '../utils/profitReportPeriodRange';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { syncXeroForProfitPeriod } from '../utils/syncXeroForProfitPeriod';

async function profitImputationChipKey(signal?: AbortSignal): Promise<string> {
  try {
    const res = await authenticatedFetch(API_ENDPOINTS.profitabilityV2.config(), { signal });
    if (!res.ok) return 'cfg-err';
    const json = (await res.json()) as {
      revision?: number;
      network_revenue_imputation?: { enabled?: boolean; avg_gbp_per_job?: number };
    };
    const imp = json.network_revenue_imputation;
    return `${json.revision ?? 0}|${imp?.enabled ? 1 : 0}|${imp?.avg_gbp_per_job ?? 0}`;
  } catch {
    return 'cfg-err';
  }
}

/** Prefer zeecore fill meta (only when rows were actually filled). */
export function revenueImputationNoticeFromMeta(
  ...metas: Array<TruthRevenueImputationMeta | null | undefined>
): ProfitRevenueImputationNotice | null {
  // Max per dimension — period + last-7 chart costs can both report the same
  // vehicle-day fills; summing would double-count. Orders/customers are separate
  // endpoints so max across those metas still surfaces any non-zero fill.
  let vehicleDaysFilled = 0;
  let ordersFilled = 0;
  let customersFilled = 0;
  let base: TruthRevenueImputationMeta | null = null;
  // Months from every endpoint: the order and vehicle-day reads can each use months the other did not.
  const monthsUsed: NonNullable<TruthRevenueImputationMeta['months_used']> = {};
  let filledAtSingleAverage = 0;
  for (const m of metas) {
    if (!m) continue;
    vehicleDaysFilled = Math.max(vehicleDaysFilled, m.vehicle_days_filled || 0);
    ordersFilled = Math.max(ordersFilled, m.orders_filled || 0);
    customersFilled = Math.max(customersFilled, m.customers_filled || 0);
    Object.assign(monthsUsed, m.months_used ?? {});
    // Customer-day rows have no depots, so every filled customer-day job counts
    // as single-average there; the page takes customer revenue from the jobs
    // instead, so only vehicle-day and job fills say how many used £/job.
    const customerDayOnly = (m.customers_filled || 0) > 0 && !m.vehicle_days_filled && !m.orders_filled;
    if (!customerDayOnly) filledAtSingleAverage = Math.max(filledAtSingleAverage, m.filled_at_single_average || 0);
    base ??= m;
  }
  if (!base || (vehicleDaysFilled === 0 && ordersFilled === 0 && customersFilled === 0)) {
    return null;
  }
  return {
    kind: 'assumed_avg_per_job',
    avgGbpPerJob: Math.round(base.avg_gbp_per_job * 100) / 100,
    monthsUsed,
    filledAtSingleAverage,
    referencePeriod: base.reference_period,
    provenance: base.provenance,
    ordersFilled,
    vehicleDaysFilled,
  };
}

function withAssumedRevenueNotes(
  bundle: ProfitPageBundle,
  notice: ProfitRevenueImputationNotice | null,
): ProfitPageBundle {
  if (!notice) return bundle;
  return {
    ...bundle,
    revenueImputation: notice,
    notes: {
      ...bundle.notes,
      detail:
        (Object.keys(notice.monthsUsed ?? {}).length > 0
          ? `Jobs without revenue use ${revenueImputationMonthsText(notice)}. `
          : `Vehicle/job/customer revenue uses an Assumed average of £${notice.avgGbpPerJob.toFixed(2)}/job `
            + `(Xero customer sales ÷ ${notice.provenance.job_count} consignments for `
            + `${notice.referencePeriod.start} → ${notice.referencePeriod.end}). `)
        + 'On the Vehicle tab it is paid to the vehicle that delivered the job; collection '
        + 'and trunk legs get £0, so each job counts once. '
        + 'Not customer rate cards — replace when cards arrive. '
        + bundle.notes.detail,
    },
  };
}

const LAST_SEVEN_CHART_TITLE = 'Daily net profit: last 7 days';

function withLastSevenDayChart(
  bundle: ProfitPageBundle,
  period: ProfitReportPeriod,
  chartCosts: TruthMergedCostRow[],
  chartRange: { startDate: string; endDate: string },
): ProfitPageBundle {
  if (period !== 'yesterday' && period !== 'last-week') return bundle;
  return {
    ...bundle,
    dailySeries: buildDailySeries(chartCosts, period, chartRange),
    chartTitle: LAST_SEVEN_CHART_TITLE,
  };
}

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError')
    || (err instanceof Error && err.name === 'AbortError')
  );
}

type ChipCacheEntry = {
  data: ProfitPageBundle | null;
  empty: boolean;
  orders: TruthMergedOrderRow[];
  costs: TruthMergedCostRow[];
  lastLoadedAt: string | null;
};

/**
 * Company KPI cards from Xero books (GET /profit-kpis). Off for now: the cards
 * always show the ops figures (vehicle-day attribution), even when Xero data is
 * available. Xero sync on Refresh is unaffected.
 */
export const XERO_COMPANY_KPIS_ENABLED = false;

type XeroKpisResult = TruthProfitKpisFetchResult | { status: 'disabled' };

/** Overlay company strip from GET /profit-kpis onto an ops (or empty-shell) bundle. */
function applyXeroCompanyKpis(
  bundle: ProfitPageBundle,
  xeroKpisResult: XeroKpisResult,
): ProfitPageBundle {
  if (xeroKpisResult.status === 'disabled') return bundle;
  if (xeroKpisResult.status === 'ok' && xeroKpisResult.data.available && xeroKpisResult.data.kpis
    && xeroKpisResult.data.kpiMode === 'xero_books') {
    const xero = xeroKpisResult.data;
    const xeroKpiNotice: ProfitXeroKpiNotice = {
      kind: 'from_xero',
      booksSource: xero.provenance?.books_source ?? null,
      pnlFrom: xero.provenance?.pnl_from,
      pnlTo: xero.provenance?.pnl_to,
      unmappedAccountCodes: xero.gaps?.unmapped_account_codes,
      unallocatedPurchaseCreditGbp: xero.gaps?.unallocated_purchase_credit_gbp,
      partialRevenueMapping: xero.provenance?.partial_revenue_mapping === true,
    };
    return {
      ...bundle,
      kpiMode: 'xero_books',
      xeroKpiNotice,
      sourcesLabel: bundle.sourcesLabel.includes('Xero')
        ? bundle.sourcesLabel
        : `Xero · ${bundle.sourcesLabel}`,
      kpis: {
        ...bundle.kpis,
        ...xero.kpis,
      },
      notes: {
        ...bundle.notes,
        kpis:
          xero.provenance?.books_source === 'profit_and_loss'
            ? 'Company KPIs from ingested Xero Profit & Loss (Income, Net Profit). Fuel, hire, motor expenses and agency staff prefer named P&L lines. Detail tables stay on ops attribution.'
            : 'Company KPIs from ingested Xero invoices/credit notes (ACCREC − network ACCPAY 331) — invoice rollup fallback when no matching P&L. Standing uses Fleet Cost depreciation until Assets ingest. Detail tables stay on ops attribution.',
        dataQualityFollowUp:
          xero.provenance?.books_source === 'profit_and_loss'
            ? 'Company cards are from the Xero P&L (Finance truth). Vehicle / job / customer rows remain Estimated allocation and will not sum to the Xero figures.'
            : 'Company cards are from Xero invoices (invoice rollup — narrower than full P&L). Vehicle / job / customer rows remain Estimated allocation and will not sum to the Xero figures.',
      },
    };
  }
  if (xeroKpisResult.status === 'ok' && !xeroKpisResult.data.available) {
    return {
      ...bundle,
      xeroKpiNotice: {
        kind: 'unavailable',
        reason: xeroKpisResult.data.reason ?? 'no_xero_docs_in_date_window',
      },
    };
  }
  if (xeroKpisResult.status === 'http_error' || xeroKpisResult.status === 'network_error') {
    return {
      ...bundle,
      xeroKpiNotice: {
        kind: 'error',
        reason:
          xeroKpisResult.status === 'http_error'
            ? `http_${xeroKpisResult.httpStatus}`
            : 'network_error',
      },
    };
  }
  return bundle;
}

export function useProfitPageBundle(
  period: ProfitReportPeriod,
  opts?: {
    enabled?: boolean;
    demo?: boolean;
    /** When false, Refresh only reloads stored figures (no Xero ingest). Admin-only action. */
    syncXero?: boolean;
    /** Company KPI cards from Xero books. Defaults to XERO_COMPANY_KPIS_ENABLED (off). */
    xeroCompanyKpis?: boolean;
  },
) {
  const enabled = opts?.enabled !== false;
  const demo = opts?.demo === true;
  const syncXero = opts?.syncXero !== false;
  const xeroCompanyKpis = opts?.xeroCompanyKpis ?? XERO_COMPANY_KPIS_ENABLED;
  const range = useMemo(() => profitReportPeriodToRange(period), [period]);
  const chartRange = useMemo(() => profitReportChartRange(period), [period]);

  const [data, setData] = useState<ProfitPageBundle | null>(null);
  const [empty, setEmpty] = useState(false);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<string | null>(null);
  /** Retained for vehicle/order map lookups (ORDERS sidebar, handoff depot). */
  const [orders, setOrders] = useState<TruthMergedOrderRow[]>([]);
  /** Map coords not filled yet because zeecore's postcode geocoder was still loading. */
  const [ordersCoordsPending, setOrdersCoordsPending] = useState(false);
  const [costs, setCosts] = useState<TruthMergedCostRow[]>([]);

  const abortRef = useRef<AbortController | null>(null);
  const chipCacheRef = useRef<Map<string, ChipCacheEntry>>(new Map());

  const figuresKey = `${demo}|${enabled}|${period}|${range.startDate}|${range.endDate}`;

  const load = useCallback(
    async (loadOpts?: { cacheBust?: boolean; skipChipCache?: boolean }) => {
      if (!enabled) {
        setData(null);
        setEmpty(false);
        setError(null);
        setOrders([]);
        setCosts([]);
        setLoading(false);
        return;
      }

      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      const impKey = demo ? 'demo' : await profitImputationChipKey(ac.signal);
      if (ac.signal.aborted) return;

      const key = `${demo}|${enabled}|${period}|${range.startDate}|${range.endDate}|${impKey}`;
      if (!loadOpts?.cacheBust && !loadOpts?.skipChipCache) {
        const cached = chipCacheRef.current.get(key);
        if (cached) {
          setData(cached.data);
          setEmpty(cached.empty);
          setOrders(cached.orders);
          setOrdersCoordsPending(false);
          setCosts(cached.costs);
          setLastLoadedAt(cached.lastLoadedAt);
          setError(null);
          setLoading(false);
          return;
        }
      }

      setLoading(true);
      setError(null);
      const fetchOpts = {
        signal: ac.signal,
        ...(loadOpts?.cacheBust ? { cacheBust: Date.now() } : {}),
      };

      try {
        if (demo) {
          const bundle = getMockProfitReport(period);
          if (ac.signal.aborted) return;
          setData(bundle);
          setEmpty(false);
          setOrders([]);
          setCosts([]);
          const loadedAt = new Date().toISOString();
          setLastLoadedAt(loadedAt);
          chipCacheRef.current.set(key, {
            data: bundle,
            empty: false,
            orders: [],
            costs: [],
            lastLoadedAt: loadedAt,
          });
          return;
        }

        const { startDate, endDate } = range;
        const chartNeedsExtraFetch =
          chartRange.startDate !== startDate || chartRange.endDate !== endDate;

        // Cards first: KPIs + vehicle-days + light status (skip heavy detail).
        // Chart may use a wider window (last 7 days for Yesterday / Last week).
        const [costsResult, chartCostsResult, status, xeroKpisResult] = await Promise.all([
          fetchTruthMergedCosts(startDate, endDate, fetchOpts),
          chartNeedsExtraFetch
            ? fetchTruthMergedCosts(chartRange.startDate, chartRange.endDate, fetchOpts)
            : Promise.resolve(null),
          fetchTruthStatus(startDate, endDate, {
            includeCoverage: false,
            probeSources: false,
            ...fetchOpts,
          }).catch(() => null),
          xeroCompanyKpis
            ? fetchTruthProfitKpis(startDate, endDate, fetchOpts)
            : Promise.resolve<XeroKpisResult>({ status: 'disabled' }),
        ]);
        if (ac.signal.aborted) return;

        const costsRows = costsResult.records;
        // Assumed £/job is applied in zeecore on merged-costs / orders / customer-daily.
        const chartCostsSource = chartCostsResult?.records ?? costsRows;
        const earlyImputationNotice = revenueImputationNoticeFromMeta(
          costsResult.revenue_imputation,
          chartCostsResult?.revenue_imputation,
        );

        // Paint company strip + cost-based tables ASAP (orders empty until detail wave).
        const earlyMapped = profitPageFromTruth({
          costs: costsRows,
          orders: [],
          customers: [],
          status,
          period,
          range: { startDate, endDate },
          chartCosts: chartCostsSource,
          chartRange,
        });

        const xeroAvailable =
          xeroKpisResult.status === 'ok'
          && xeroKpisResult.data.available
          && xeroKpisResult.data.kpiMode === 'xero_books'
          && Boolean(xeroKpisResult.data.kpis);
        if (earlyMapped.kind === 'empty' && !xeroAvailable) {
          setData(null);
          setEmpty(true);
          setOrders([]);
          setCosts([]);
          setLastLoadedAt(new Date().toISOString());
          chipCacheRef.current.set(key, {
            data: null,
            empty: true,
            orders: [],
            costs: [],
            lastLoadedAt: new Date().toISOString(),
          });
          return;
        }

        let earlyBundle: ProfitPageBundle;
        if (earlyMapped.kind === 'empty') {
          earlyBundle = withAssumedRevenueNotes(
            withLastSevenDayChart(
              applyXeroCompanyKpis(
                emptyProfitReportShell(period, { startDate, endDate }, false),
                xeroKpisResult,
              ),
              period,
              chartCostsSource,
              chartRange,
            ),
            earlyImputationNotice,
          );
        } else {
          earlyBundle = withAssumedRevenueNotes(
            withLastSevenDayChart(
              applyXeroCompanyKpis(earlyMapped.bundle, xeroKpisResult),
              period,
              chartCostsSource,
              chartRange,
            ),
            earlyImputationNotice,
          );
        }
        const otherWork = (costsResult.other_work ?? []).map(otherWorkLineText);
        setData({ ...earlyBundle, otherWork });
        setEmpty(false);
        setCosts(costsRows);
        setOrders([]);
        // Cards are usable — drop the full-page spinner before detail finishes.
        if (abortRef.current === ac) setLoading(false);

        // Detail wave: orders + customers (already Assumed-filled by zeecore when enabled).
        const [ordersResult, customersResult, ordersSeries] = await Promise.all([
          fetchTruthMergedOrders(startDate, endDate, fetchOpts),
          fetchTruthCustomerProfitabilityDaily(startDate, endDate, fetchOpts),
          // Admin cross-check only: a failure must not break the page.
          fetchOrdersDailySeries(startDate, endDate, { signal: ac.signal })
            .then((r) => ('preparing' in r ? null : r.days))
            .catch(() => null),
        ]);
        if (ac.signal.aborted) return;

        const ordersRows = ordersResult.rows;
        const customers = customersResult.rows;
        const imputationNotice = revenueImputationNoticeFromMeta(
          costsResult.revenue_imputation,
          chartCostsResult?.revenue_imputation,
          ordersResult.revenue_imputation,
          customersResult.revenue_imputation,
        );

        const mapped = profitPageFromTruth({
          costs: costsRows,
          orders: ordersRows,
          customers,
          customersAwaiting: customersResult.awaiting_merged_order,
          networkCost: ordersResult.network_cost,
          ordersSeries,
          status,
          period,
          range: { startDate, endDate },
          chartCosts: chartCostsSource,
          chartRange,
        });

        let finalBundle: ProfitPageBundle;
        let finalOrders: TruthMergedOrderRow[] = [];
        let finalCosts: TruthMergedCostRow[] = costsRows;

        if (mapped.kind === 'empty') {
          finalBundle = withAssumedRevenueNotes(
            withLastSevenDayChart(
              applyXeroCompanyKpis(
                emptyProfitReportShell(period, { startDate, endDate }, false),
                xeroKpisResult,
              ),
              period,
              chartCostsSource,
              chartRange,
            ),
            imputationNotice,
          );
          finalOrders = [];
          finalCosts = [];
        } else {
          const withXero = applyXeroCompanyKpis(mapped.bundle, xeroKpisResult);
          finalBundle = withAssumedRevenueNotes(
            withLastSevenDayChart(
              withXero,
              period,
              chartCostsSource,
              chartRange,
            ),
            imputationNotice,
          );
          finalOrders = ordersRows;
          finalCosts = costsRows;
        }

        finalBundle = { ...finalBundle, otherWork };
        setData(finalBundle);
        setEmpty(false);
        setOrders(finalOrders);
        setCosts(finalCosts);
        setOrdersCoordsPending(ordersResult.coordsPending);
        const loadedAt = new Date().toISOString();
        setLastLoadedAt(loadedAt);
        // Do not cache orders whose map coords were still loading: revisiting
        // the chip must refetch, not show "no locations" until a hard refresh.
        if (!ordersResult.coordsPending) {
          chipCacheRef.current.set(key, {
            data: finalBundle,
            empty: false,
            orders: finalOrders,
            costs: finalCosts,
            lastLoadedAt: loadedAt,
          });
        }
      } catch (err) {
        if (isAbortError(err) || ac.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to load profit figures');
        setData(null);
        setEmpty(false);
        setOrders([]);
        setCosts([]);
      } finally {
        if (abortRef.current === ac) setLoading(false);
      }
    },
    [demo, enabled, period, range, chartRange, xeroCompanyKpis],
  );

  const sourceKey = `${demo}|${enabled}`;
  const loadedSourceKeyRef = useRef<string | null>(null);
  const loadedFiguresKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const previousSourceKey = loadedSourceKeyRef.current;
    const previousFiguresKey = loadedFiguresKeyRef.current;
    loadedSourceKeyRef.current = sourceKey;
    loadedFiguresKeyRef.current = figuresKey;
    const sourceChanged = previousSourceKey !== sourceKey;
    const periodChanged = previousFiguresKey != null && previousFiguresKey !== figuresKey;

    if (sourceChanged) {
      chipCacheRef.current.clear();
      setData(null);
      setEmpty(false);
      setOrders([]);
      setCosts([]);
      setLastLoadedAt(null);
      setError(null);
    } else if (periodChanged) {
      const cached = chipCacheRef.current.get(figuresKey);
      if (cached) {
        setData(cached.data);
        setEmpty(cached.empty);
        setOrders(cached.orders);
        setOrdersCoordsPending(false);
        setCosts(cached.costs);
        setLastLoadedAt(cached.lastLoadedAt);
        setError(null);
        setLoading(false);
        return () => {
          abortRef.current?.abort();
        };
      }
      setData(null);
      setEmpty(false);
      setOrders([]);
      setCosts([]);
      setError(null);
      // load() awaits the imputation chip key before it sets loading; without
      // this the cleared figures render as an empty period in the meantime.
      setLoading(true);
    }
    void load();
    return () => {
      abortRef.current?.abort();
    };
  }, [load, figuresKey, sourceKey]);

  /**
   * Sync Xero invoices + P&L for the current Profit period, wait for the
   * exact-window P&L to land, then reload figures.
   */
  const refresh = useCallback(async () => {
    if (!enabled || demo || !syncXero) {
      chipCacheRef.current.delete(figuresKey);
      await load({ cacheBust: true, skipChipCache: true });
      return;
    }
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);
    chipCacheRef.current.delete(figuresKey);
    try {
      const sync = await syncXeroForProfitPeriod(range.startDate, range.endDate, {
        signal: ac.signal,
      });
      if (ac.signal.aborted) return;
      if (!sync.ok) {
        setError(sync.error);
      } else {
        const settleDeadline = Date.now() + 60_000;
        while (Date.now() < settleDeadline) {
          if (ac.signal.aborted) return;
          const probe = await fetchTruthProfitKpis(range.startDate, range.endDate, {
            signal: ac.signal,
            cacheBust: Date.now(),
          });
          if (
            probe.status === 'ok'
            && probe.data.provenance?.books_source === 'profit_and_loss'
          ) {
            break;
          }
          await new Promise<void>((resolve, reject) => {
            const t = setTimeout(resolve, 2_000);
            ac.signal.addEventListener(
              'abort',
              () => {
                clearTimeout(t);
                reject(new DOMException('Aborted', 'AbortError'));
              },
              { once: true },
            );
          });
        }
      }
      if (ac.signal.aborted) return;
      await load({ cacheBust: true, skipChipCache: true });
    } catch (err) {
      if (isAbortError(err) || ac.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to refresh profit figures');
      setLoading(false);
    }
  }, [demo, enabled, syncXero, figuresKey, load, range.endDate, range.startDate]);

  return {
    range,
    data,
    empty,
    loading,
    error,
    lastLoadedAt,
    orders,
    ordersCoordsPending,
    costs,
    refresh,
  };
}
